import { useState } from 'react';
import axios from 'axios';
import { Upload, Mail, User, MessageSquare, Send, X, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { FileIcon } from '../fileIcons';

function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export default function SendFiles() {
  const [files, setFiles] = useState([]);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState('');

  const handleFiles = (newFiles) => {
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...Array.from(newFiles).filter(f => !existing.has(f.name + f.size))];
    });
  };

  const send = async () => {
    setError('');
    if (!files.length) { setError('Dodaj przynajmniej jeden plik'); return; }
    if (!senderEmail.trim() || !senderEmail.includes('@')) { setError('Podaj prawidłowy adres email'); return; }
    setUploading(true); setUploadProgress(0);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      formData.append('senderEmail', senderEmail);
      formData.append('senderName', senderName);
      formData.append('message', message);
      await axios.post('/api/inbox', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Błąd podczas wysyłania. Spróbuj ponownie.');
    } finally {
      setUploading(false); setUploadProgress(0);
    }
  };

  // ═════ STYLE ═════
  const wrapStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1rem',
    background: '#fafaf9',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  };

  const meshBlob1 = { position: 'absolute', top: '-30%', left: '-15%', width: '75%', height: '120%', background: 'radial-gradient(circle, rgba(139,92,246,0.32) 0%, transparent 55%)', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 };
  const meshBlob2 = { position: 'absolute', bottom: '-25%', right: '-10%', width: '65%', height: '90%', background: 'radial-gradient(circle, rgba(56,189,248,0.25) 0%, transparent 55%)', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 };
  const meshBlob3 = { position: 'absolute', top: '15%', right: '10%', width: '30%', height: '40%', background: 'radial-gradient(circle, rgba(244,114,182,0.22) 0%, transparent 60%)', filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0 };

  const cardStyle = {
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    borderRadius: '20px',
    padding: '30px',
    maxWidth: '460px',
    width: '100%',
    border: '1px solid rgba(255,255,255,0.8)',
    boxShadow: '0 30px 80px rgba(139,92,246,0.15), 0 8px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(139,92,246,0.05)',
    position: 'relative',
    zIndex: 1
  };

  const gradientText = {
    background: 'linear-gradient(135deg,#0a0a0c 0%,#7c3aed 50%,#0ea5e9 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent'
  };

  const brandStyle = { ...gradientText, fontSize: '10px', fontWeight: 700, letterSpacing: '0.22em', marginBottom: '14px', textAlign: 'center' };
  const titleStyle = { ...gradientText, fontSize: '25px', fontWeight: 600, letterSpacing: '-0.028em', lineHeight: 1.15, textAlign: 'center', marginBottom: '6px' };
  const subStyle = { fontSize: '12px', color: 'rgba(10,10,12,0.6)', textAlign: 'center', marginBottom: '20px' };

  const dropStyle = (active) => ({
    border: `1.5px dashed ${active ? '#7c3aed' : 'rgba(139,92,246,0.35)'}`,
    background: active ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.4)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '12px',
    padding: '20px 14px',
    textAlign: 'center',
    cursor: 'pointer',
    marginBottom: '14px',
    transition: 'all 0.2s'
  });

  const fileItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 12px',
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(15px)',
    WebkitBackdropFilter: 'blur(15px)',
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: '10px',
    marginBottom: '5px'
  };

  const labelStyle = { fontSize: '9.5px', color: 'rgba(10,10,12,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' };

const inputStyle = (fieldName) => ({
    width: '100%',
    background: '#fff',
    border: `1.5px solid ${focused === fieldName ? '#7c3aed' : 'rgba(139,92,246,0.3)'}`,
    borderRadius: '10px',
    padding: '11px 13px',
    color: '#0a0a0c',
    fontSize: '13px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
    boxShadow: focused === fieldName ? '0 0 0 3px rgba(139,92,246,0.18)' : '0 2px 6px rgba(139,92,246,0.08)',
    transition: 'all 0.15s'
  });

  const btnDisabled = uploading || !files.length || !senderEmail;
  const btnStyle = {
    width: '100%',
    background: btnDisabled ? 'rgba(0,0,0,0.1)' : 'linear-gradient(135deg,#a78bfa,#38bdf8)',
    color: btnDisabled ? 'rgba(0,0,0,0.4)' : '#0a0a0c',
    border: 'none',
    borderRadius: '12px',
    padding: '14px',
    fontSize: '13.5px',
    fontWeight: 600,
    cursor: btnDisabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
    letterSpacing: '-0.005em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: btnDisabled ? 'none' : '0 6px 24px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
    marginTop: '14px',
    transition: 'all 0.15s'
  };

  const footStyle = {
    textAlign: 'center',
    marginTop: '18px',
    paddingTop: '14px',
    borderTop: '1px solid rgba(139,92,246,0.12)',
    fontSize: '10.5px',
    color: 'rgba(10,10,12,0.55)',
    lineHeight: 1.7
  };
  const footLink = { color: '#7c3aed', textDecoration: 'none', fontWeight: 500 };

  // ═════ SUCCESS STATE ═════
  if (done) {
    return (
      <div style={wrapStyle}>
        <div style={meshBlob1}></div>
        <div style={meshBlob2}></div>
        <div style={meshBlob3}></div>
        <div style={{ ...cardStyle, padding: '3rem 2.5rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem', color: '#10b981' }}>
            <CheckCircle size={56} strokeWidth={1.5} />
          </div>
          <div style={brandStyle}>MT CAPITAL</div>
          <h1 style={{ ...titleStyle, fontSize: '24px', marginBottom: '10px' }}>Pliki wysłane!</h1>
          <p style={{ fontSize: '13.5px', color: 'rgba(10,10,12,0.65)', marginBottom: '1.75rem', lineHeight: 1.55 }}>
            Twoja przesyłka dotarła do MT Capital. Zostaniesz poinformowany o jej odbiorze na adres <strong style={{ color: '#0a0a0c' }}>{senderEmail}</strong>.
          </p>
          <button
            onClick={() => { setFiles([]); setSenderEmail(''); setSenderName(''); setMessage(''); setDone(false); }}
            style={{ ...btnStyle, marginTop: 0 }}
          >
            <Send size={14} strokeWidth={2} />
            Wyślij kolejne pliki
          </button>
          <div style={footStyle}>
            <Shield size={11} strokeWidth={1.75} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '3px', color: 'rgba(10,10,12,0.55)' }} />
            Twoje pliki są szyfrowane · <a href="/panel" style={footLink}>Logowanie</a>
            <br />
            <a href="/privacy" style={footLink}>Polityka Prywatności</a>
            {' · © 2026 MT Capital'}
          </div>
        </div>
      </div>
    );
  }

  // ═════ MAIN FORM ═════
  return (
    <div style={wrapStyle}>
      <div style={meshBlob1}></div>
      <div style={meshBlob2}></div>
      <div style={meshBlob3}></div>
      <div style={cardStyle}>
        <div style={brandStyle}>MT CAPITAL</div>
        <h1 style={titleStyle}>Prześlij pliki</h1>
        <p style={subStyle}>Bezpiecznie, szybko, bez konta</p>

        <div
          onClick={() => document.getElementById('sendFilesInput').click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
          style={dropStyle(dragging)}
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '7px', color: '#7c3aed' }}>
            <Upload size={30} strokeWidth={1.75} />
          </div>
          <div style={{ fontSize: '12.5px', color: '#0a0a0c', fontWeight: 500, marginBottom: '3px' }}>
            Przeciągnij pliki lub kliknij
          </div>
          <div style={{ fontSize: '10.5px', color: 'rgba(10,10,12,0.5)' }}>
            Maksymalnie 2 GB · Dowolny format
          </div>
        </div>
        <input
          type="file"
          id="sendFilesInput"
          multiple
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />

        {files.length > 0 && (
          <div style={{ marginBottom: '14px', maxHeight: '160px', overflowY: 'auto' }}>
            {files.map((f, i) => (
              <div key={i} style={fileItemStyle}>
                <FileIcon name={f.name} boxSize={26} size={13} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 500, color: '#0a0a0c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </div>
                  <div style={{ fontSize: '10.5px', color: 'rgba(10,10,12,0.45)' }}>
                    {formatSize(f.size)}
                  </div>
                </div>
                <button
                  onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(10,10,12,0.4)', padding: '4px', display: 'flex', alignItems: 'center' }}
                  aria-label="Usuń"
                >
                  <X size={14} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginBottom: '10px' }}>
          <div style={labelStyle}>
            <Mail size={12} strokeWidth={2} style={{ color: focused === 'email' ? '#7c3aed' : 'rgba(10,10,12,0.5)' }} />
            Twój email *
          </div>
          <input
            type="email"
            value={senderEmail}
            onChange={e => setSenderEmail(e.target.value)}
            onFocus={() => setFocused('email')}
            onBlur={() => setFocused('')}
            placeholder="anna.kowalska@example.com"
            autoComplete="email"
            style={inputStyle('email')}
          />
        </div>

        <div style={{ marginBottom: '10px' }}>
          <div style={labelStyle}>
            <User size={12} strokeWidth={2} style={{ color: focused === 'name' ? '#7c3aed' : 'rgba(10,10,12,0.5)' }} />
            Imię i nazwisko (opcjonalnie)
          </div>
          <input
            type="text"
            value={senderName}
            onChange={e => setSenderName(e.target.value)}
            onFocus={() => setFocused('name')}
            onBlur={() => setFocused('')}
            placeholder="Anna Kowalska"
            autoComplete="name"
            style={inputStyle('name')}
          />
        </div>

        <div style={{ marginBottom: '10px' }}>
          <div style={labelStyle}>
            <MessageSquare size={12} strokeWidth={2} style={{ color: focused === 'msg' ? '#7c3aed' : 'rgba(10,10,12,0.5)' }} />
            Wiadomość (opcjonalnie)
          </div>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            onFocus={() => setFocused('msg')}
            onBlur={() => setFocused('')}
            rows="2"
            placeholder="Dokumenty do umowy..."
            style={{ ...inputStyle('msg'), resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {error && (
          <div style={{ color: '#dc2626', fontSize: '12.5px', marginBottom: '12px', padding: '10px 13px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertCircle size={14} strokeWidth={2} />
            {error}
          </div>
        )}

        {uploading && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '11.5px', color: 'rgba(10,10,12,0.6)', marginBottom: '6px', fontWeight: 500 }}>
              Wysyłanie... {uploadProgress}%
            </div>
            <div style={{ height: '5px', background: 'rgba(0,0,0,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: uploadProgress + '%', background: 'linear-gradient(90deg,#a78bfa,#38bdf8)', transition: 'width 0.2s' }}></div>
            </div>
          </div>
        )}

        <button onClick={send} disabled={btnDisabled} style={btnStyle}>
          <Send size={14} strokeWidth={2} />
          {uploading ? 'Wysyłanie...' : `Wyślij ${files.length > 0 ? `${files.length} ${files.length === 1 ? 'plik' : 'pliki'}` : 'pliki'}`}
        </button>

        <div style={footStyle}>
          <Shield size={11} strokeWidth={1.75} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '3px', color: 'rgba(10,10,12,0.55)' }} />
          Twoje pliki są szyfrowane · <a href="/panel" style={footLink}>Logowanie</a>
          <br />
          <a href="/privacy" style={footLink}>Polityka Prywatności</a>
          {' · © 2026 MT Capital'}
        </div>
      </div>
    </div>
  );
}